import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, Check, AlertCircle, Info, ExternalLink, Clock, DollarSign, ChevronDown, ChevronRight, FileText, ShieldCheck, Search, Lock, ArrowRight, TriangleAlert, Flag } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAppContext } from "@/lib/onboardingContext";
import { useSubscription } from "@/lib/subscriptionContext";
import { getCityPermitConfig } from "@/lib/cityPermitData";
import { getCityData } from "@/lib/locationData";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { toast } from "sonner";
import { handleExternalClick } from "@/lib/externalLink";

export default function Permits() {
  const navigate = useNavigate();
  const { results: rawResults, beginnerMode, ensureResults } = useAppContext();
  const results = rawResults ?? ensureResults();
  const { subscription } = useSubscription();
  const isFree = (subscription?.plan ?? "free") === "free";
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [filter, setFilter] = useState<"all" | "required" | "optional">("all");
  const [search, setSearch] = useState("");

  // results is guaranteed non-null via ensureResults() above.

  const cityConfig = getCityPermitConfig(results.city || "");
  const allPermits = results.permits;
  
  const filtered = allPermits.filter(p => {
    if (filter === "required" && !p.required) return false;
    if (filter === "optional" && p.required) return false;
    if (search && !p.name.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const required = filtered.filter(p => p.required);
  const optional = filtered.filter(p => !p.required);

  const totalCostRange = (() => {
    const costs = allPermits.filter(p => p.required).map(p => p.estimatedCost);
    return costs.length > 0 ? `${costs.length} permits` : "";
  })();

  const toggle = (name: string) => {
    setExpanded(prev => {
      const next = new Set(prev);
      next.has(name) ? next.delete(name) : next.add(name);
      return next;
    });
  };

  const expandAll = () => {
    if (expanded.size === filtered.length) {
      setExpanded(new Set());
    } else {
      setExpanded(new Set(filtered.map(p => p.name)));
    }
  };

  const PermitCard = ({ p, i }: { p: typeof results.permits[0]; i: number }) => {
    const isOpen = expanded.has(p.name);
    return (
      <motion.div
        key={p.name}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: i * 0.03 }}
        className="bg-card rounded-xl border border-border shadow-card overflow-hidden"
      >
        <button onClick={() => toggle(p.name)} className="w-full p-4 text-left">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 flex-1 min-w-0">
              {p.required
                ? <span className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center shrink-0"><Check className="w-3.5 h-3.5 text-primary" /></span>
                : <span className="w-6 h-6 rounded-full bg-muted flex items-center justify-center shrink-0"><AlertCircle className="w-3.5 h-3.5 text-muted-foreground" /></span>
              }
              <div className="min-w-0 flex-1">
                <h3 className="font-medium text-foreground text-sm truncate">{p.name}</h3>
                {beginnerMode && <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{p.simple}</p>}
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0 ml-2">
              <span className="text-xs font-medium text-primary bg-primary/5 px-2 py-0.5 rounded-full hidden sm:inline">{p.estimatedCost}</span>
              {isOpen ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
            </div>
          </div>
        </button>

        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="overflow-hidden"
            >
              <div className="px-4 pb-4 space-y-3 border-t border-border pt-3">
                {beginnerMode && (
                  <p className="text-sm text-muted-foreground bg-muted/30 rounded-lg p-3">{p.simple}</p>
                )}
                
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-muted/50 rounded-lg p-3">
                    <div className="flex items-center gap-1.5 text-muted-foreground mb-1">
                      <Clock className="w-3.5 h-3.5" />
                      <span className="text-xs font-medium">Processing Time</span>
                    </div>
                    <p className="text-sm font-medium text-foreground">{p.processingTime}</p>
                  </div>
                  <div className="bg-muted/50 rounded-lg p-3">
                    <div className="flex items-center gap-1.5 text-muted-foreground mb-1">
                      <DollarSign className="w-3.5 h-3.5" />
                      <span className="text-xs font-medium">Estimated Cost</span>
                    </div>
                    <p className="text-sm font-medium text-foreground">{p.estimatedCost}</p>
                  </div>
                </div>

                <div>
                  <div className="flex items-center gap-1.5 text-muted-foreground mb-2">
                    <FileText className="w-3.5 h-3.5" />
                    <span className="text-xs font-medium">What You Need</span>
                  </div>
                  <ul className="space-y-1.5">
                    {p.requirements.map((req, j) => (
                      <li key={j} className="text-sm text-muted-foreground flex items-start gap-2">
                        <Check className="w-3 h-3 text-primary shrink-0 mt-0.5" />
                        {req}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Official link section */}
                <div className="bg-muted/30 rounded-lg p-3 space-y-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <a
                      href={p.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={handleExternalClick}
                      className="inline-flex items-center gap-2 text-sm bg-primary text-primary-foreground hover:bg-primary/90 px-4 py-2 rounded-lg font-medium transition-colors"
                    >
                      <ExternalLink className="w-3.5 h-3.5" />
                      Apply Now — Official Site
                    </a>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-accent/20 cursor-help">
                            <TriangleAlert className="w-3.5 h-3.5 text-accent-foreground" />
                          </span>
                        </TooltipTrigger>
                        <TooltipContent side="top" className="max-w-[260px] text-xs">
                          Government websites occasionally change URLs. If this link doesn't work, search "{p.name} {results.city}" on Google.
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                    <button
                      onClick={() => {
                        toast.success("Broken link reported", { description: `We'll check the link for "${p.name}" and update it.` });
                      }}
                      className="inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground transition-colors ml-auto"
                    >
                      <Flag className="w-3 h-3" />
                      Report broken link
                    </button>
                  </div>
                  <p className="text-[10px] text-muted-foreground leading-tight select-all font-mono break-all">{p.link}</p>
                  <p className="text-[10px] text-muted-foreground/70 leading-tight">
                    If this link doesn't work, search <span className="font-medium text-muted-foreground">"{p.name} {results.city}"</span> on Google.
                  </p>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    );
  };

  return (
    <div className="min-h-screen bg-background pb-16">
      <div className="border-b border-border bg-card">
        <div className="container py-4 flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate("/dashboard")}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="font-heading text-lg font-medium text-foreground">
              Permits & Licenses{cityConfig ? ` — ${cityConfig.city}, ${cityConfig.state}` : ""}
            </h1>
            <p className="text-sm text-muted-foreground">
              {cityConfig ? `${allPermits.length} verified permits for your city` : "What you may need — with details"}
            </p>
          </div>
        </div>
      </div>

      <div className="container pt-6 max-w-lg">
        {/* Verified badge */}
        {cityConfig && (
          <div className="bg-primary/5 border border-primary/20 rounded-xl p-3 mb-4 flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-primary shrink-0" />
            <p className="text-xs text-muted-foreground">
              <span className="font-medium text-foreground">Real government data</span> · Costs verified from official .gov sources · Last updated: {cityConfig.lastVerified}
            </p>
          </div>
        )}

        {/* City-specific warnings (moved from onboarding) */}
        {(() => {
          const cityData = getCityData(results.city || "");
          if (!cityData?.warnings?.length) return null;
          return (
            <div className="bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-900/40 rounded-xl p-3 mb-4">
              <div className="flex items-center gap-2 mb-2">
                <TriangleAlert className="w-4 h-4 text-amber-600 dark:text-amber-400 shrink-0" />
                <p className="text-xs font-medium text-foreground">
                  Heads up for {cityData.name}
                </p>
              </div>
              <ul className="space-y-1.5">
                {cityData.warnings.map((w, i) => (
                  <li key={i} className="text-xs text-muted-foreground flex items-start gap-2">
                    <span className="w-1 h-1 rounded-full bg-amber-500 shrink-0 mt-1.5" />
                    <span>{w}</span>
                  </li>
                ))}
              </ul>
            </div>
          );
        })()}

        {/* Summary stats */}
        <div className="grid grid-cols-3 gap-3 mb-4">
          <div className="bg-card border border-border rounded-xl p-3 text-center">
            <p className="text-2xl font-medium text-foreground">{allPermits.filter(p => p.required).length}</p>
            <p className="text-xs text-muted-foreground">Required</p>
          </div>
          <div className="bg-card border border-border rounded-xl p-3 text-center">
            <p className="text-2xl font-medium text-foreground">{allPermits.filter(p => !p.required).length}</p>
            <p className="text-xs text-muted-foreground">Optional</p>
          </div>
          <div className="bg-card border border-border rounded-xl p-3 text-center">
            <p className="text-2xl font-medium text-foreground">{allPermits.length}</p>
            <p className="text-xs text-muted-foreground">Total</p>
          </div>
        </div>

        {/* Search + Filter bar */}
        <div className="flex gap-2 mb-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search permits..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full pl-9 pr-3 py-2 text-sm bg-card border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 text-foreground placeholder:text-muted-foreground"
            />
          </div>
          <button
            onClick={expandAll}
            className="px-3 py-2 text-xs font-medium bg-card border border-border rounded-lg hover:bg-muted/50 transition-colors text-muted-foreground"
          >
            {expanded.size === filtered.length ? "Collapse" : "Expand All"}
          </button>
        </div>

        {/* Filter tabs */}
        <div className="flex gap-2 mb-6">
          {(["all", "required", "optional"] as const).map(f => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-colors ${
                filter === f
                  ? "bg-primary text-primary-foreground border-primary"
                  : "bg-card border-border text-muted-foreground hover:bg-muted/50"
              }`}
            >
              {f === "all" ? "All" : f === "required" ? "Required" : "Optional"}
            </button>
          ))}
        </div>

        {/* Info banner */}
        <div className="bg-accent/10 border border-accent/20 rounded-xl p-4 mb-6 flex items-start gap-3">
          <Info className="w-5 h-5 text-accent shrink-0 mt-0.5" />
          <p className="text-sm text-muted-foreground">
            {cityConfig 
              ? `These are the exact permits required in ${cityConfig.city}. Tap any permit for costs, timelines, and direct application links.`
              : "Requirements depend on city, county, state, and business model. Always verify with local authorities."}
          </p>
        </div>

        {/* Required permits */}
        {required.length > 0 && (
          <>
            <h2 className="font-heading font-medium text-foreground mb-3 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-primary" />
              Required ({required.length})
            </h2>
            <div className="space-y-2 mb-8">
              {required.map((p, i) => {
                if (isFree && i >= 2) {
                  // Blurred locked permit for free users
                  if (i === 2) {
                    return (
                      <div key="locked-permits" className="relative rounded-xl overflow-hidden">
                        <div className="absolute inset-0 bg-card/60 backdrop-blur-md z-10" />
                        <div className="blur-sm pointer-events-none select-none space-y-2">
                          {required.slice(2).map((lp, li) => (
                            <div key={lp.name} className="bg-card rounded-xl border border-border p-4">
                              <span className="text-sm text-foreground">{lp.name}</span>
                            </div>
                          ))}
                        </div>
                        <div className="absolute inset-0 z-20 flex flex-col items-center justify-center text-center px-6">
                          <Lock className="w-6 h-6 text-muted-foreground mb-2" />
                          <p className="text-sm font-medium text-foreground mb-1">
                            {required.length - 2} more permits locked
                          </p>
                          <p className="text-xs text-muted-foreground mb-3">Upgrade to Pro ($99/mo) to see all permits</p>
                          <Button size="sm" className="gap-1.5" onClick={() => navigate("/pricing")}>
                            Upgrade to Pro <ArrowRight className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    );
                  }
                  return null;
                }
                return <PermitCard key={p.name} p={p} i={i} />;
              })}
            </div>
          </>
        )}

        {/* Optional permits */}
        {optional.length > 0 && !isFree && (
          <>
            <h2 className="font-heading font-medium text-foreground mb-3 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-muted-foreground" />
              May Be Needed ({optional.length})
            </h2>
            <div className="space-y-2">
              {optional.map((p, i) => <PermitCard key={p.name} p={p} i={i} />)}
            </div>
          </>
        )}

        {filtered.length === 0 && (
          <div className="text-center py-12 text-muted-foreground">
            <p className="text-sm">No permits match your search.</p>
          </div>
        )}
      </div>
    </div>
  );
}
